import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '@/lib/auth/entra/msal';

/**
 * SharePoint document access via Microsoft Graph, using the signed-in
 * manager's delegated token (Sites.ReadWrite.All, admin-consented). Files live
 * under one document library (VITE_SHAREPOINT_DRIVE_ID), one folder per
 * location (named by store code). The app never holds an app-only SharePoint
 * credential — access is the user's own, scoped by their SharePoint membership.
 */

const GRAPH = 'https://graph.microsoft.com/v1.0';
const GRAPH_SCOPES = ['https://graph.microsoft.com/Sites.ReadWrite.All'];

export const SHAREPOINT_DRIVE_ID = import.meta.env.VITE_SHAREPOINT_DRIVE_ID ?? '';
export const sharePointConfigured = SHAREPOINT_DRIVE_ID.length > 0;

export interface DriveFile {
  id: string;
  name: string;
  size: number;
  webUrl: string;
  lastModifiedDateTime: string;
  isFolder: boolean;
  mimeType: string | null;
}

async function graphToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new Error('not signed in');
  try {
    const r = await msalInstance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
    return r.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const r = await msalInstance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
      return r.accessToken;
    }
    throw err;
  }
}

async function graph<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await graphToken();
  const res = await fetch(`${GRAPH}${path}`, {
    ...init,
    headers: { authorization: `Bearer ${token}`, ...(init.headers as Record<string, string>) },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      detail = body?.error?.message ?? '';
    } catch {
      // ignore
    }
    throw new Error(`Graph ${res.status}${detail ? ` — ${detail}` : ''}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const enc = encodeURIComponent;

/** Ensure the per-location folder exists; returns its name (the path segment). */
export async function ensureLocationFolder(folderName: string): Promise<void> {
  try {
    await graph(`/drives/${SHAREPOINT_DRIVE_ID}/root:/${enc(folderName)}`);
    return; // exists
  } catch {
    // create below
  }
  await graph(`/drives/${SHAREPOINT_DRIVE_ID}/root/children`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      name: folderName,
      folder: {},
      '@microsoft.graph.conflictBehavior': 'fail',
    }),
  });
}

export async function listLocationFiles(folderName: string): Promise<DriveFile[]> {
  await ensureLocationFolder(folderName);
  const data = await graph<{
    value: {
      id: string;
      name: string;
      size: number;
      webUrl: string;
      lastModifiedDateTime: string;
      folder?: unknown;
      file?: { mimeType?: string };
    }[];
  }>(
    `/drives/${SHAREPOINT_DRIVE_ID}/root:/${enc(folderName)}:/children?$select=id,name,size,webUrl,lastModifiedDateTime,folder,file&$orderby=name`,
  );
  return data.value
    .filter((i) => !i.folder)
    .map((i) => ({
      id: i.id,
      name: i.name,
      size: i.size,
      webUrl: i.webUrl,
      lastModifiedDateTime: i.lastModifiedDateTime,
      isFolder: false,
      mimeType: i.file?.mimeType ?? null,
    }));
}

/** Simple upload (supports up to 250 MB per Graph). */
export async function uploadLocationFile(folderName: string, file: File): Promise<void> {
  await ensureLocationFolder(folderName);
  const path = `/drives/${SHAREPOINT_DRIVE_ID}/root:/${enc(folderName)}/${enc(file.name)}:/content`;
  await graph(path, {
    method: 'PUT',
    headers: { 'content-type': file.type || 'application/octet-stream' },
    body: file,
  });
}

export async function deleteLocationFile(itemId: string): Promise<void> {
  await graph(`/drives/${SHAREPOINT_DRIVE_ID}/items/${itemId}`, { method: 'DELETE' });
}

/** Short-lived embeddable preview URL for inline rendering. */
export async function previewUrl(itemId: string): Promise<string | null> {
  try {
    const r = await graph<{ getUrl?: string }>(
      `/drives/${SHAREPOINT_DRIVE_ID}/items/${itemId}/preview`,
      { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' },
    );
    return r.getUrl ?? null;
  } catch {
    return null;
  }
}
